import { useResource } from '@landsbankinn/l16n';
import {
  Box,
  Col,
  InputContainer,
  InputValidationMessage,
  Label,
  PatternInput,
} from '@landsbankinn/ui-kit';
import { useRef, useState } from 'react';
import { useScreenSize } from 'src/hooks/useScreenSize';
import {
  AllowedLoginType,
  useApplicationClientContext,
  useApplicationServerContext,
  type LoginServerState,
} from 'src/state';
import { DebugUserSelector } from '../DebugUserSelector';
import { ExternalAuthTypesLink } from '../ExternalAuthTypesLink';
import { LoginButtons } from '../LoginButtons';
import { LoginForm } from '../LoginForm';
import { res } from './PkiSim.res';
import { PkiSimLoadingWithCode } from './PkiSimCode';
import { usePkiSimSubmit } from './usePkiSimSubmit';
import { usePkiSimValidation } from './usePkiSimValidation';

export function PkiSim({ loginButtonHidden }: { loginButtonHidden?: boolean }) {
  const { r } = useResource();
  const { isMobile } = useScreenSize();
  const [submitWithPostCode, setSubmitWithPostCode] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const phoneNumberRef = useRef<HTMLInputElement>(null);

  const [serverState] = useApplicationServerContext() as [LoginServerState];
  const [, clientDispatch] = useApplicationClientContext();
  const { onPhonenumberChanged, pkisimState, onXsrf } = usePkiSimValidation();

  const submit = usePkiSimSubmit({
    onCode: (code: string) => {
      // set code to show to the user
      setSubmitWithPostCode(code);
    },
    onSubmitting: (isSubmitting: boolean) => {
      // some submit running
      setIsSubmitting(isSubmitting);
      clientDispatch({ type: 'setIsSubmitting', payload: { isSubmitting } });
    },
    onError: (error: string) => {
      // error
      clientDispatch({
        type: 'setLoginErrorState',
        payload: { errorMessage: error },
      });
    },
    onXsrf: (xsrfToken: string) => {
      // got a new xsrf token
      onXsrf(xsrfToken);
    },
  });

  return (
    <LoginForm
      form={serverState.forms.pkiSim}
      fieldValueProvider={(name) => {
        if (name === 'xsrf' && pkisimState.xsrf) {
          return pkisimState.xsrf;
        }
        return serverState.forms.pkiSim.fields[name].value;
      }}
      onSubmit={(e) => {
        if (pkisimState.phonenumber.validationState !== 'valid') {
          setShowValidation(true);
          return;
        }
        e.preventDefault();
        if (!isSubmitting) {
          submit(
            pkisimState.phonenumber.value,
            serverState.forms.pkiSim.method,
            pkisimState.xsrf,
          );
        }
      }}
    >
      {isSubmitting && (
        <PkiSimLoadingWithCode code={submitWithPostCode || ''} />
      )}
      {!isSubmitting && (
        <>
          <Box marginBottom={isMobile ? '0' : '36'}>
            <Col paddingTop="36">
              <InputContainer
                marginBottom="20"
                direction="col"
                width="100%"
                validationState={pkisimState.phonenumber.validationState}
                variant="text input col with validation"
              >
                <Label>{r(res.phonenumber)}</Label>
                <PatternInput
                  required={true}
                  instrumentAs="enter_phone_number"
                  inputMode="tel"
                  format="### ####"
                  showMask={false}
                  id="phonenumber"
                  variant="default"
                  autoComplete="off"
                  name={serverState.forms.pkiSim.fields.phoneNumber.name}
                  autoFocus={false}
                  ref={phoneNumberRef}
                  value={pkisimState.phonenumber.value}
                  onPaste={(e) => {
                    onPhonenumberChanged(
                      e.clipboardData
                        .getData('text')
                        .replaceAll(/\s/g, '')
                        .slice(-7),
                    );
                  }}
                  onValueChange={(v) => {
                    onPhonenumberChanged(v.value.slice(-7));
                  }}
                />

                {showValidation && (
                  <InputValidationMessage
                    invalidMessage={r(res.validationPhonenumberInvalid)}
                    unsetMessage={r(res.validationPhonenumberRequired)}
                  />
                )}
              </InputContainer>
              <DebugUserSelector
                inputref={phoneNumberRef}
                debugValues={serverState.forms.pkiSim.fields.phoneNumber.debugValues?.filter(
                  (u) => u.value,
                )}
                onUserSelect={({ value }) => {
                  onPhonenumberChanged(value.slice(-7));
                }}
              />

              <ExternalAuthTypesLink label={r(res.linkTitle)} />
            </Col>
          </Box>
          <LoginButtons
            showLoginButton={!loginButtonHidden}
            showRejectButton={serverState.isConsentFlow}
            isValid={pkisimState.phonenumber.validationState === 'valid'}
            loginType={AllowedLoginType.PkiSim}
          />
        </>
      )}
    </LoginForm>
  );
}
